import React from 'react';
import { Outlet } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Container,
  useTheme
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

const drawerWidth = 240;

function Admin() {
  const location = useLocation();
  const theme = useTheme();
  
  const isActive = (path) => {
    return location.pathname === path || 
      (path !== '/admin' && location.pathname.startsWith(path));
  };
  
  const menuItems = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/admin',
      active: isActive('/admin') && location.pathname === '/admin'
    },
    { 
      text: 'Site Settings', 
      icon: <SettingsIcon />, 
      path: '/admin/settings',
      active: isActive('/admin/settings')
    },
    { 
      text: 'Content Management', 
      icon: <EditIcon />, 
      path: '/admin/content',
      active: isActive('/admin/content')
    },
    { 
      text: 'Media Library', 
      icon: <ImageIcon />, 
      path: '/admin/media',
      active: isActive('/admin/media')
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.secondary.main
        }}
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Dozer Admin
          </Typography>
          <Box 
            component={Link}
            to="/"
            sx={{ 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            <ExitToAppIcon sx={{ mr: 1 }} />
            <Typography variant="body2">Back to Site</Typography>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Side Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { 
            width: drawerWidth, 
            boxSizing: 'border-box',
            backgroundColor: '#f5f5f7',
            borderRight: 'none',
            boxShadow: '1px 0 10px rgba(0, 0, 0, 0.05)'
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', px: 2, py: 2 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem 
                key={item.text} 
                component={Link} 
                to={item.path}
                selected={item.active}
                sx={{ 
                  mb: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 113, 227, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 113, 227, 0.12)',
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  color: item.active ? theme.palette.primary.main : theme.palette.text.secondary,
                  minWidth: 40
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{ 
                    fontWeight: item.active ? 600 : 500,
                    color: item.active ? theme.palette.primary.main : theme.palette.text.primary
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      
      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}

export default Admin; 